/**
 * Pure helpers for DataTableAction cell truncation, column width sync, and tooltips.
 * DOM-dependent orchestration lives in CellTruncationManager.js.
 */

export const TRUNCATABLE_CELL_SELECTOR = [
  'td.data-table-action__body-cell .data-table-action__truncatable',
  'td.data-table-action__body-cell .single-line',
  'td.data-table-action__body-cell .line-clamp-1',
  'td.data-table-action__body-cell .status-indicator__label',
  'td.data-table-action__body-cell .v-chip',
  'td.data-table-action__body-cell .v-chip__content',
  'td.data-table-action__body-cell .request-table__ref-cell',
  'td.data-table-action__body-cell > span',
  'td.data-table-action__body-cell > button',
  'td.data-table-action__body-cell > div > span',
  'td.data-table-action__body-cell > div.d-flex',
].join(',');

const NON_DATA_TABLE_CELL_CLASSES = [
  'checkbox-cell',
  'action-data',
  'tasklist__table-row-leading-cell',
  'data-table-action__empty-cell',
];

/**
 * @param {string|number|null|undefined} raw
 * @returns {number|null}
 */
export function parseColumnWidth(raw) {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'number' && !Number.isNaN(raw)) return raw;
  const str = String(raw).trim();
  const pxMatch = str.match(/^(\d+(?:\.\d+)?)\s*px$/i);
  if (pxMatch) return parseFloat(pxMatch[1]);
  const numMatch = str.match(/^(\d+(?:\.\d+)?)$/);
  if (numMatch) return parseFloat(numMatch[1]);
  const vhMatch = str.match(/^(\d+(?:\.\d+)?)\s*vh$/i);
  if (vhMatch) {
    return (parseFloat(vhMatch[1]) / 100) * window.innerHeight;
  }
  return null;
}

/**
 * @param {object} header
 * @param {{ enableCellTruncate: boolean, defaultColumnMaxWidth: number }} options
 * @returns {Record<string, string>}
 */
export function getHeaderColumnStyle(header, { enableCellTruncate, defaultColumnMaxWidth }) {
  if (!enableCellTruncate || header.value === 'action' || header.noTruncate) {
    return {};
  }
  const maxW = header.maxWidth
    ? parseColumnWidth(header.maxWidth)
    : defaultColumnMaxWidth;
  if (!maxW) return {};
  return {
    maxWidth: `${maxW}px`,
  };
}

/**
 * @param {Array<object>} headers
 * @returns {Array<object>}
 */
export function getTruncatableDataHeaders(headers) {
  return headers.filter(
    (header) => header.visible && header.value !== 'action' && !header.noTruncate,
  );
}

/**
 * @param {object|null|undefined} scopedSlots
 * @returns {boolean}
 */
export function hasCustomTbodySlot(scopedSlots) {
  return Boolean(scopedSlots && scopedSlots.tbody);
}

/**
 * @param {Element|null|undefined} tbody
 * @returns {string}
 */
export function computeTbodySlotContentKey(tbody) {
  if (!tbody) return '';
  const rows = tbody.querySelectorAll('tr:not(.data-table-action__empty-row)');
  if (!rows.length) return 'empty';
  return Array.from(rows)
    .map((row) => row.textContent?.replace(/\s+/g, ' ').trim().slice(0, 120))
    .join('\0');
}

/**
 * @param {Element|null|undefined} cell
 * @returns {boolean}
 */
export function isNonDataTableCell(cell) {
  if (!cell || cell.tagName !== 'TD') return true;
  return NON_DATA_TABLE_CELL_CLASSES.some((className) => cell.classList.contains(className));
}

/**
 * @param {Element} row
 * @param {{ bulkActions: boolean }} options
 * @returns {Element[]}
 */
export function getRowDataCells(row, { bulkActions }) {
  const tdCells = [...row.children].filter((cell) => cell.tagName === 'TD');
  const skipLeadingBulkCell = bulkActions
    && tdCells.length > 0
    && !isNonDataTableCell(tdCells[0]);

  return tdCells.filter((cell, index) => {
    if (skipLeadingBulkCell && index === 0) return false;
    return !isNonDataTableCell(cell);
  });
}

/**
 * Match th/td by data-column-value without interpolating colValue into a CSS selector.
 *
 * @param {Element|null|undefined} root
 * @param {string} colValue
 * @param {string[]} tagNames
 * @returns {Element[]}
 */
export function findElementsByColumnValue(root, colValue, tagNames = ['TH', 'TD']) {
  if (!root || !colValue) return [];
  const tagSet = new Set(tagNames);
  return Array.from(root.querySelectorAll('th, td')).filter((el) => (
    tagSet.has(el.tagName) && el.getAttribute('data-column-value') === colValue
  ));
}

/**
 * @param {Element} th
 * @param {object} header
 * @param {{ enableCellTruncate: boolean, defaultColumnMaxWidth: number }} options
 * @returns {number|null}
 */
export function resolveHeaderMaxWidthPx(th, header, options) {
  if (!th) return null;
  const inlineMax = th.style.maxWidth;
  if (inlineMax) {
    const parsed = parseColumnWidth(inlineMax);
    if (parsed) return parsed;
  }
  const computedMax = window.getComputedStyle(th).maxWidth;
  if (computedMax && computedMax !== 'none') {
    const parsed = parseColumnWidth(computedMax);
    if (parsed) return parsed;
  }
  const columnStyle = getHeaderColumnStyle(header, options);
  if (columnStyle.maxWidth) {
    return parseFloat(columnStyle.maxWidth);
  }
  return null;
}

/**
 * @param {Element} el
 * @returns {boolean}
 */
export function shouldSkipTruncationElement(el) {
  const td = el.closest('td');
  if (!td) return true;
  if (td.classList.contains('data-table-action__empty-cell')) return true;
  if (td.classList.contains('checkbox-cell') || td.classList.contains('action-data')) return true;
  if (td.classList.contains('tasklist__table-row-leading-cell')) return true;
  if (td.querySelector('.v-checkbox') && el.closest('.v-input--selection-controls')) return true;
  if (el.closest('.tasklist__row-actions-trigger, .tasklist__table-checkbox, [data-row-nav-ignore]')) return true;
  if (el.tagName === 'BUTTON' || el.tagName === 'A') return true;
  if (el.classList.contains('width-fit') || el.classList.contains('w-fit')) return true;
  if (el.closest('.width-fit, .w-fit')) return true;
  if (el.closest('.task-approver-cell, .overflow-list, .v-avatar, .fc-tooltip__activator, .fc-system-actor')) return true;
  return false;
}

/**
 * @param {Element|null|undefined} el
 * @returns {boolean}
 */
export function isTruncatedElement(el) {
  if (!el) return false;
  if (el.scrollWidth > el.clientWidth) return true;
  const parent = el.parentElement;
  if (parent && parent !== el.closest('td') && parent.scrollWidth > parent.clientWidth) {
    return true;
  }
  const td = el.closest('td');
  return td ? td.scrollWidth > td.clientWidth : false;
}

/**
 * @param {Node|null|undefined} node
 * @param {Element|null|undefined} rootEl
 * @returns {Element|null}
 */
export function findTruncatableTarget(node, rootEl) {
  if (!node || node === rootEl) return null;
  if (node.closest([
    '.checkbox-cell',
    '.action-data',
    '.tasklist__table-row-leading-cell',
    '.tasklist__row-actions-trigger',
    '.tasklist__table-checkbox',
    '[data-row-nav-ignore]',
    '.data-table-action__empty-cell',
  ].join(', '))) {
    return null;
  }
  if (node.closest('.v-tooltip, .v-input--selection-controls, button, a')) return null;
  if (node.closest('.task-approver-cell, .overflow-list, .v-avatar, .fc-tooltip__activator, .fc-system-actor')) return null;
  if (node.closest('.width-fit, .w-fit')) return null;
  if (node.classList?.contains('data-table-action__truncatable')) return node;
  if (node.classList?.contains('single-line')) return node;
  if (node.classList?.contains('line-clamp-1')) return node;
  if (node.classList?.contains('status-indicator__label')) return node;
  if (node.classList?.contains('v-chip') || node.classList?.contains('v-chip__content')) return node;
  if (node.tagName === 'SPAN') {
    const td = node.closest('td.data-table-action__body-cell');
    if (td) return node;
  }
  if (node.classList?.contains('d-flex') && node.closest('td.data-table-action__body-cell')) {
    return node;
  }
  return null;
}
